"use client";

import Link from "next/link";
import { ArrowLeft, BookOpen, Search, Ruler, Paintbrush, Settings, Zap, ChevronRight, HelpCircle, Home, Wrench, Truck, CreditCard, RotateCcw } from "lucide-react";
import { useState } from "react";

const wikiCategories = [
  {
    title: "Measuring & Sizing",
    icon: Ruler,
    description: "Learn how to measure your windows correctly",
    articles: [
      { title: "How to Measure for Inside Mount", link: "/guides/measuring" },
      { title: "How to Measure for Outside Mount", link: "/guides/mounting" },
      { title: "Measuring Bay Windows", link: "/guides/measuring-bay-windows" },
      { title: "Measuring Sliding Glass Doors", link: "/guides/measuring-sliding-doors" },
      { title: "Measuring Arched Windows", link: "/guides/measuring-arched-windows" },
      { title: "Measuring Skylights", link: "/guides/measuring-skylights" },
    ]
  },
  {
    title: "Product Guides",
    icon: BookOpen,
    description: "In-depth guides for each product type",
    articles: [
      { title: "Cellular Shades Benefits & Features", link: "/guides/cellular-shades" },
      { title: "Roller Shades Explained", link: "/guides/roller-shades" },
      { title: "Motorized Blinds Setup", link: "/guides/motorized-blinds" },
      { title: "Zebra Blinds vs Roller Shades", link: "/zebra-blinds-vs-roller-shades" },
      { title: "Solar Shade Openness: 1% vs 3% vs 5%", link: "/solar-shade-openness-1-vs-3-vs-5" },
      { title: "Sheer Shades vs Zebra Blinds", link: "/sheer-shades-vs-zebra-blinds" },
    ]
  },
  {
    title: "Installation",
    icon: Wrench,
    description: "Step-by-step installation instructions",
    articles: [
      { title: "DIY Installation Guide", link: "/guides/installation" },
      { title: "Tools You'll Need", link: "/guides/installation" },
      { title: "Inside vs Outside Mount", link: "/guides/mounting" },
      { title: "Professional Installation", link: "/measure-install" },
    ]
  },
  {
    title: "Care & Maintenance",
    icon: Paintbrush,
    description: "Keep your blinds looking great",
    articles: [
      { title: "Cleaning Your Blinds", link: "/guides/care-maintenance" },
      { title: "Maintaining Wood Blinds", link: "/guides/care-maintenance" },
      { title: "Caring for Fabric Shades", link: "/guides/care-maintenance" },
      { title: "Motorized System Care", link: "/guides/motorized-blinds" },
    ]
  },
  {
    title: "Choosing the Right Product",
    icon: HelpCircle,
    description: "Find the perfect blinds for your needs",
    articles: [
      { title: "Blinds vs Shades: What's the Difference?", link: "/guides/choosing-blinds" },
      { title: "Best Blinds for Bedrooms", link: "/guides/choosing-blinds" },
      { title: "Best Blinds for Bathrooms", link: "/bathroom-blinds" },
      { title: "Light Control Options", link: "/guides/choosing-blinds" },
      { title: "Energy Efficient Window Treatments", link: "/guides/cellular-shades" },
    ]
  },
  {
    title: "Room Ideas",
    icon: Home,
    description: "Inspiration for every room",
    articles: [
      { title: "Living Room Window Ideas", link: "/products?room=living-room" },
      { title: "Bedroom Window Treatments", link: "/products?room=bedroom" },
      { title: "Kitchen & Bathroom Solutions", link: "/products?room=kitchen" },
      { title: "Home Office Setup", link: "/products?room=office" },
    ]
  },
];

const quickLinks = [
  { title: "Ordering & Shipping", icon: Truck, link: "/faq", description: "Shipping times, tracking, and delivery" },
  { title: "Returns", icon: RotateCcw, link: "/returns", description: "Our return policy and guarantees" },
  { title: "Payment Options", icon: CreditCard, link: "/faq", description: "Accepted payment methods" },
  { title: "Professional Services", icon: Settings, link: "/measure-install", description: "Measure & install services" },
];

const popularArticles = [
  { title: "How to Measure Windows for Blinds", link: "/guides/measuring", category: "Measuring" },
  { title: "Inside Mount vs Outside Mount", link: "/guides/mounting", category: "Installation" },
  { title: "Wood vs Faux Wood Blinds", link: "/guides/choosing-blinds", category: "Product Guide" },
  { title: "DIY Installation Step-by-Step", link: "/guides/installation", category: "Installation" },
  { title: "Cellular Shades for Energy Savings", link: "/guides/cellular-shades", category: "Product Guide" },
  { title: "Motorized Blinds Setup Guide", link: "/guides/motorized-blinds", category: "Product Guide" },
];

export default function WikiPage() {
  const [searchQuery, setSearchQuery] = useState("");

  // Filter articles based on search
  const filteredCategories = searchQuery
    ? wikiCategories.map(cat => ({
        ...cat,
        articles: cat.articles.filter(article =>
          article.title.toLowerCase().includes(searchQuery.toLowerCase())
        )
      })).filter(cat => cat.articles.length > 0)
    : wikiCategories;

  return (
    <div className="min-h-screen bg-sb">
      {/* Hero Section */}
      <div className="bg-charcoal text-white py-8 sm:py-12">
        <div className="container mx-auto px-3 sm:px-4">
          <Link href="/" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-3 sm:mb-4 transition-colors text-sm">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3">
            <BookOpen className="w-7 h-7 sm:w-10 sm:h-10" />
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold">Help Center</h1>
          </div>
          <p className="text-white/90 max-w-2xl mb-4 sm:mb-6 text-sm sm:text-base">
            Find answers to common questions, browse guides, and learn everything about window treatments.
          </p>

          {/* Search */}
          <div className="max-w-xl relative">
            <Search className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
            <input maxLength={80}
              type="text"
              placeholder="Search for help articles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 sm:pl-12 pr-4 py-2.5 sm:py-3 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-white text-sm sm:text-base"
            />
          </div>
        </div>
      </div>

      <div className="container mx-auto px-3 sm:px-4 py-8 sm:py-12">
        {/* Quick Links */}
        <section className="mb-8 sm:mb-12">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {quickLinks.map((item, index) => (
              <Link
                key={index}
                href={item.link}
                className="bg-white p-3 sm:p-5 rounded-lg sm:rounded-xl border border-gray-200 hover:border-charcoal hover:shadow-md transition-all group"
              >
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-sb-alt rounded-lg flex items-center justify-center mb-2 sm:mb-3 group-hover:bg-charcoal transition-colors">
                  <item.icon className="w-4 h-4 sm:w-5 sm:h-5 text-sb-gold group-hover:text-white transition-colors" />
                </div>
                <h3 className="font-bold text-heading-charcoal mb-0.5 sm:mb-1 text-sm sm:text-base">{item.title}</h3>
                <p className="text-sb-text-muted text-xs sm:text-sm line-clamp-2">{item.description}</p>
              </Link>
            ))}
          </div>
        </section>

        {/* Popular Articles */}
        <section className="mb-8 sm:mb-12">
          <div className="flex items-center gap-2 mb-4 sm:mb-6">
            <Zap className="w-5 h-5 sm:w-6 sm:h-6 text-sb-gold" />
            <h2 className="text-xl sm:text-2xl font-bold text-heading-charcoal">Popular Articles</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
            {popularArticles.map((article, index) => (
              <Link
                key={index}
                href={article.link}
                className="bg-white p-3 sm:p-4 rounded-lg border border-gray-200 hover:border-charcoal hover:shadow-md transition-all group flex items-start justify-between"
              >
                <div>
                  <span className="text-xs text-sb-gold font-medium">{article.category}</span>
                  <h3 className="font-medium text-heading-charcoal group-hover:text-sb-gold transition-colors mt-0.5 sm:mt-1 text-sm sm:text-base">
                    {article.title}
                  </h3>
                </div>
                <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 group-hover:text-sb-gold transition-colors flex-shrink-0 mt-0.5" />
              </Link>
            ))}
          </div>
        </section>

        {/* Categories */}
        <section>
          <h2 className="text-xl sm:text-2xl font-bold text-heading-charcoal mb-4 sm:mb-6">Browse by Topic</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {filteredCategories.map((category, index) => (
              <div key={index} className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="bg-sb p-3 sm:p-4 border-b border-gray-100">
                  <div className="flex items-center gap-2 sm:gap-3">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-charcoal rounded-lg flex items-center justify-center flex-shrink-0">
                      <category.icon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-bold text-heading-charcoal text-sm sm:text-base">{category.title}</h3>
                      <p className="text-gray-500 text-xs truncate">{category.description}</p>
                    </div>
                  </div>
                </div>
                <ul className="divide-y divide-gray-100">
                  {category.articles.slice(0, 5).map((article, articleIndex) => (
                    <li key={articleIndex}>
                      <Link
                        href={article.link}
                        className="flex items-center justify-between p-2.5 sm:p-3 hover:bg-sb-bg transition-colors group"
                      >
                        <span className="text-gray-700 text-xs sm:text-sm group-hover:text-sb-gold transition-colors line-clamp-1">
                          {article.title}
                        </span>
                        <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-sb-gold transition-colors flex-shrink-0 ml-2" />
                      </Link>
                    </li>
                  ))}
                  {category.articles.length > 5 && (
                    <li className="p-2.5 sm:p-3 text-center">
                      <span className="text-xs sm:text-sm text-gray-500">+{category.articles.length - 5} more articles</span>
                    </li>
                  )}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Still Need Help */}
        <section className="bg-charcoal mt-8 sm:mt-12 rounded-lg sm:rounded-xl p-5 sm:p-8 text-center text-white">
          <h2 className="text-xl sm:text-2xl font-bold mb-2 sm:mb-3">Still Have Questions?</h2>
          <p className="text-white/80 mb-4 sm:mb-6 max-w-xl mx-auto text-sm sm:text-base">
            Our expert team is here to help. Contact us for personalized assistance.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
            <Link
              href="/contact"
              className="inline-flex items-center gap-2 bg-primary-red text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-lg font-semibold hover:bg-primary-dark transition-colors text-sm sm:text-base w-full sm:w-auto justify-center"
            >
              Contact Us
            </Link>
            <Link
              href="/consultation"
              className="inline-flex items-center gap-2 bg-white/10 text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-lg font-semibold hover:bg-white/20 transition-colors text-sm sm:text-base w-full sm:w-auto justify-center"
            >
              Book Free Consultation
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
}
